import { useQuery } from '@apollo/client'
import { gql } from '__generated__'
import { GetCompsPlansInfoQuery, SubscriptionFeatureComps } from '__generated__/graphql'

// GraphQL query to get all subscription products and the user's current plan
const GET_COMPS_PLANS_INFO = gql(/* GraphQL */`
  query GetCompsPlansInfo {
    billingSubscriptionProducts {
      id
      name
      features {
        ... on SubscriptionFeatureComps {
          limits {
            interval
            value
          }
        }
      }
    }
    
    myEnterprise {
      id
      subscription {
        ... on BillingSubscriptionActive {
          id
          plan {
            id
            product {
              id
              features {
                ... on SubscriptionFeatureComps {
                  limits {
                    interval
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`)

/**
 * Hook to determine if there are plans with more comps than the user's current plan.
 * Returns true if such plans exist, which means the user could benefit from upgrading.
 */
export const useHasMoreCompsPlans = () => {
  const { data, loading, error } = useQuery(GET_COMPS_PLANS_INFO)

  if (loading || error || !data)
    return { hasMoreCompsPlans: false, currentCompsAllowance: 0, maxCompsAvailable: 0, loading, error }

  // Get the current subscription
  const subscription = data.myEnterprise?.subscription

  // If there's no subscription or it's not active, consider all plans as having more comps
  if (!subscription || subscription.__typename !== 'BillingSubscriptionActive')
    return { hasMoreCompsPlans: true, currentCompsAllowance: 0, maxCompsAvailable: getMaxCompsValue(data), loading: false, error: null }

  // Get the current plan's comps limit
  const currentPlanFeatures = subscription.plan?.product?.features || []
  const currentCompsAllowance = getCompsValue(currentPlanFeatures)

  // Find the maximum comps available across all plans
  const maxCompsAvailable = getMaxCompsValue(data)

  // Determine if there are plans with more comps
  // If current plan already has unlimited comps (Infinity), no plan can have more
  // Otherwise, check if maxCompsAvailable is greater than current allowance
  const hasMoreCompsPlans = currentCompsAllowance === Infinity ? false : maxCompsAvailable > currentCompsAllowance

  return {
    hasMoreCompsPlans,
    currentCompsAllowance,
    maxCompsAvailable,
    loading: false,
    error: null,
  }
}

/**
 * Helper function to extract the comps value from feature array
 */
const getCompsValue = (features: GetCompsPlansInfoQuery['billingSubscriptionProducts'][number]['features']): number => {
  // Find the SubscriptionFeatureComps feature
  const compsFeature = features.find(f => f.__typename === 'SubscriptionFeatureComps') as SubscriptionFeatureComps | undefined

  if (!compsFeature)
    return 0

  // If comps feature exists but has no limits or empty limits array,
  // it means unlimited comps (represented as Infinity)
  if (!compsFeature.limits || compsFeature.limits.length === 0)
    return Infinity

  // Get the highest value limit
  return Math.max(...compsFeature.limits.map(limit => limit.value || 0))
}

/**
 * Helper function to find the maximum comps value across all plans
 */
const getMaxCompsValue = (data: GetCompsPlansInfoQuery): number => {
  if (!data.billingSubscriptionProducts)
    return 0

  // Map each product to its comps limit
  const compsLimits = data.billingSubscriptionProducts.map(product => getCompsValue(product.features))

  // Return the highest comps limit
  return Math.max(0, ...compsLimits)
}
